import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabasePublishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const LOCAL_DEV_TOKEN = 'local-dev-token';

export const supabase =
  supabaseUrl && supabasePublishableKey
    ? createClient(supabaseUrl, supabasePublishableKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      })
    : null;

export const AUTH_CHANGED_EVENT = 'fakturaone:auth-changed';

function toError(error, fallbackMessage) {
  if (error instanceof Error && error.message) {
    return error;
  }
  const message = error?.message || fallbackMessage || 'Unbekannter Fehler';
  return new Error(message);
}

function emitAuthChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

function hasLocalDevToken() {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('token') === LOCAL_DEV_TOKEN;
}

function buildStoredUser(user) {
  if (!user) {
    return null;
  }

  const userMetadata = user.user_metadata || {};
  const provider = user.app_metadata?.provider;

  return {
    user_id: user.id,
    email: user.email,
    name:
      userMetadata.name ||
      userMetadata.full_name ||
      userMetadata.display_name ||
      user.email?.split('@')[0] ||
      'Benutzer',
    auth_type: provider === 'google' ? 'google' : 'supabase',
    picture: userMetadata.avatar_url || userMetadata.picture || null,
  };
}

export function clearStoredAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  emitAuthChanged();
}

export function syncSessionStorage(session) {
  if (!session?.access_token || !session?.user) {
    if (hasLocalDevToken()) {
      return null;
    }
    clearStoredAuth();
    return null;
  }

  localStorage.setItem('token', session.access_token);
  const storedUser = buildStoredUser(session.user);
  if (storedUser) {
    localStorage.setItem('user', JSON.stringify(storedUser));
  }
  emitAuthChanged();
  return storedUser;
}

export async function getSessionWithSync() {
  if (!supabase) {
    throw new Error('Supabase Auth ist im Frontend nicht konfiguriert');
  }

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw error;
  }

  syncSessionStorage(data.session);
  return data.session;
}

export async function requestEmailChange(newEmail, redirectTo) {
  if (!supabase) {
    throw new Error('Supabase Auth ist nicht konfiguriert');
  }

  const email = String(newEmail || '').trim();
  const options = {};
  if (redirectTo) {
    options.emailRedirectTo = redirectTo;
  }

  const { data, error } = await supabase.auth.updateUser(
    { email },
    Object.keys(options).length ? options : undefined
  );
  if (error) {
    throw toError(error, 'E-Mail konnte nicht geändert werden');
  }
  return data;
}

export async function changePasswordWithReauth({ email, currentPassword, newPassword }) {
  if (!supabase) {
    throw new Error('Supabase Auth ist nicht konfiguriert');
  }

  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) {
    throw new Error('Keine E-Mail-Adresse gefunden');
  }

  const { error: reauthError } = await supabase.auth.signInWithPassword({
    email: normalizedEmail,
    password: currentPassword,
  });
  if (reauthError) {
    throw toError(reauthError, 'Aktuelles Passwort ist falsch');
  }

  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    throw toError(error, 'Passwort konnte nicht geändert werden');
  }
  return data;
}

export async function requestPasswordResetEmail(email, redirectTo) {
  if (!supabase) {
    throw new Error('Supabase Auth ist nicht konfiguriert');
  }

  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) {
    throw new Error('Keine E-Mail-Adresse gefunden');
  }

  const { data, error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
    redirectTo: redirectTo || `${window.location.origin}/auth/callback`,
  });
  if (error) {
    throw toError(error, 'Passwort-Reset-E-Mail konnte nicht gesendet werden');
  }
  return data;
}

export async function sendAccountDeletionVerificationEmail(email, redirectTo) {
  if (!supabase) {
    throw new Error('Supabase Auth ist nicht konfiguriert');
  }

  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) {
    throw new Error('Keine E-Mail-Adresse gefunden');
  }

  const emailRedirectTo = redirectTo || `${window.location.origin}/auth/callback?delete_account=1`;
  const { data, error } = await supabase.auth.signInWithOtp({
    email: normalizedEmail,
    options: { emailRedirectTo },
  });
  if (error) {
    throw toError(error, 'Bestätigungs-E-Mail konnte nicht gesendet werden');
  }
  return data;
}
